/**
 * useChat Hook
 * Custom React hook for managing chat state and interactions
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { ChatMessage } from '../types/chat';
import { sendChatMessage } from '../services/chatService';

export interface UseChatReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (content: string, backendContent?: string) => Promise<void>;
  clearError: () => void;
  sessionId: string | null;
  loadChat: (chatId: string, chatMessages: ChatMessage[], backendSessionId?: string | null) => void;
  clearChat: () => void;
}

export function useChat(): UseChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = useCallback(
    async (content: string, backendContent?: string) => {
      if (!content.trim()) return;

      // Add user message immediately
      const userMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        sessionId: sessionId || '',
        role: 'user',
        content: content.trim(),
        createdAt: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);

      try {
        // Send to backend
        const response = await sendChatMessage({
          sessionId: sessionId || undefined,
          message: (backendContent || content).trim(),
        });

        // Update session ID if this is the first message
        if (!sessionId && response.sessionId) {
          setSessionId(response.sessionId);
        }

        // Add assistant response
        setMessages((prev) => [...prev, response.message]);
      } catch (err) {
        console.error('[useChat] Error:', err);
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to send message. Please try again.'
        );

        // Add error message to chat
        const errorMessage: ChatMessage = {
          id: `error-${Date.now()}`,
          sessionId: sessionId || '',
          role: 'assistant',
          content:
            'Sorry, I encountered an error. Please try again or contact support if the problem persists.',
          createdAt: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const loadChat = useCallback((chatId: string, chatMessages: ChatMessage[], backendSessionId?: string | null) => {
    const restoredSessionId = backendSessionId
      || [...chatMessages].reverse().find(message => message.sessionId)?.sessionId
      || chatId;
    setSessionId(restoredSessionId);
    setMessages(chatMessages);
    setError(null);
  }, []);

  const clearChat = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearError,
    sessionId,
    loadChat,
    clearChat,
  };
}
